import React from 'react'
import { Modal as BaseModal, ModalBody, ModalFooter, SIZE, ROLE, ModalHeader } from 'baseui/modal'
import { useStyletron } from 'baseui'
import { Grid, Cell, ALIGNMENT } from 'baseui/layout-grid'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes } from '@fortawesome/free-solid-svg-icons'
import { ourColors } from './Colors'
import { Heading } from './Heading'
import { HeadingLevel } from 'baseui/heading'
import { Button } from './Button'

interface ModalProps {
  readonly headerText: string
  readonly footer?: React.ReactNode | readonly React.ReactNode[]
  isOpen: boolean
  setIsOpen: (value: boolean) => void
}

interface FooterProps {
  yesCallback: () => void
  setModalOpen: (status: boolean) => void
  yesText: string
  noText: string
  isLoading: boolean
  setIsLoading: (status: boolean) => void
}

export const YesNoFooter = ({
  yesCallback,
  setModalOpen,
  yesText,
  noText,
  isLoading,
  setIsLoading
}: FooterProps) => {
  return (
    <>
      <Button
        kind="secondary"
        isLoading={isLoading}
        data-testid="confirm-resolve-toggle-button"
        onClick={() => {
          setIsLoading(true)
          yesCallback()
        }}
        overrides={{
          BaseButton: {
            style: {
              width: '40%',
              marginTop: '0px',
              marginRight: '15px',
              paddingBottom: '14px',
              paddingTop: '14px',
              marginBottom: '0px',
              minHeight: '50px',
              height: '50px'
            }
          }
        }}
      >
        {yesText}
      </Button>
      <Button
        kind="tertiary"
        data-testid="close-modal-button"
        onClick={() => setModalOpen(false)}
        overrides={{
          BaseButton: {
            style: {
              width: '40%',
              marginTop: '0px',
              minHeight: '50px',
              height: '50px'
            }
          }
        }}
      >
        {noText}
      </Button>
    </>
  )
}

export const Modal = (props: React.PropsWithChildren<ModalProps>) => {
  const [css] = useStyletron()

  const closeButtonStyles = {
    border: 'none',
    justifyContent: 'flex-end',
    ':hover': {
      cursor: 'pointer'
    },
    background: 'white',
    paddingRight: '0px'
  }
  const headingOverrides = {
    Block: { style: { marginBottom: 0, marginTop: 0, color: ourColors.copyGrey } }
  }

  return (
    <BaseModal
      onClose={() => props.setIsOpen(false)}
      closeable={false}
      isOpen={props.isOpen}
      animate
      autoFocus
      size={SIZE.default}
      role={ROLE.dialog}
    >
      <ModalHeader>
        <Grid gridMargins={0} gridGutters={0}>
          <Cell span={[3, 5]} align={ALIGNMENT.start}>
            <HeadingLevel>
              <Heading styleLevel={3} overrides={headingOverrides}>
                {props.headerText}
              </Heading>
            </HeadingLevel>
          </Cell>
          <Cell span={1}>
            <div
              className={css({
                display: 'flex',
                justifyContent: 'flex-end'
              })}
            >
              <button
                className={css(closeButtonStyles)}
                onClick={() => props.setIsOpen(false)}
                data-testid="close-alert"
                aria-label="Close alert"
              >
                <FontAwesomeIcon
                  className={css({
                    fontSize: '28px',
                    lineHeight: '28px',
                    justifySelf: 'right'
                  })}
                  icon={faTimes}
                  color={ourColors.copyGrey}
                />
              </button>
            </div>
          </Cell>
        </Grid>
      </ModalHeader>
      <ModalBody>{props.children}</ModalBody>
      <ModalFooter>
        <Grid gridMargins={0} gridGutters={0}>
          <Cell span={[4, 8]} align={ALIGNMENT.end}>
            {props.footer}
          </Cell>
        </Grid>
      </ModalFooter>
    </BaseModal>
  )
}
